import * as React from 'react';
import { UserState } from 'app/reducers/state';
import { Spinner, Card, Elevation, H4 } from '@blueprintjs/core';
import { executeQueryGetAuth } from 'app/utils/api';
import { each, filter, isEqual } from 'lodash';

import Highcharts from 'highcharts';
import ReactHighcharts from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';

highchartsMore(Highcharts);

import moment from 'moment';
import ReactTable from 'react-table';

interface Props {
    user: UserState;
    classID: string;
    scenario: string;
};

interface State {
    loadingStats: boolean;
    classEnrollment: any;
    stats: GameData[] | undefined;
}

interface GameData {
    postFeedback: string;
    preFeedback: string;
    userID: string;
    objectives: GameDataObjective[];
    submitted: string;
    "game-id": string;
    scenario: string;
    results: string;
}

interface GameDataObjective {
    academicDescription: string;
    category: string;
    description: string;
    name: string;
    result: string;
    submitted: string;
}

//

// we can easily move these elsewhere any
function getTotalValue (categoryTotal: any) {
    let total = 0.0;

    each(categoryTotal, cat => {
        total += cat.percent
    });

    return total;
}

function sortStatBlock (stat: any) {
    // submitted
    
    stat.sort(function (a, b) {
        const aMoment = moment(a['submitted']);
        const bMoment = moment(b['submitted']);

        return !aMoment.isBefore(bMoment);
    });
    
    return stat;
}

function checkResultString(result: any) {
    return (result === true || result === "true" || result === "True")
}

function processStatByCategory(stat: GameData) {
    let categoryTotal = {};

    each(stat.objectives, objective => {
        if (categoryTotal[objective.category] === undefined) {
            categoryTotal[objective.category] = {
                total: 1,
                working: (checkResultString(objective.result)) ? 1 : 0
            }
        }
        else {
            categoryTotal[objective.category].working += (checkResultString(objective.result)) ? 1 : 0;
            categoryTotal[objective.category].total += 1;
        }

        categoryTotal[objective.category]["percent"] = (parseFloat(categoryTotal[objective.category].working) / parseFloat(categoryTotal[objective.category].total)) * 100.0;
    });

    return categoryTotal;
}

function getBestPlaysForStudents(stats: GameData[]) {
    let userMap = {};

    each(stats, stat => {
        const userID = stat["user-id"];

        if (userMap[userID] === undefined) {
            userMap[userID] = stat;
            userMap[userID].categories = processStatByCategory(stat)
        }
        else {
            const newCategories = processStatByCategory(stat);

            const oldTotalForUser = getTotalValue(userMap[userID].categories);
            const newTotalForUser = getTotalValue(newCategories);

            if (newTotalForUser >= oldTotalForUser) {
                userMap[userID] = stat;
                userMap[userID].categories = newCategories;
            }
        }
    });

    return userMap;
}

function getInitialPlaysForStudents(stats: GameData[]) {
    let userMap = {};

    each(stats, stat => {
        const userID = stat["user-id"];

        if (userMap[userID] === undefined) {
            userMap[userID] = stat;
            userMap[userID].categories = processStatByCategory(stat)
        }
    });

    return userMap;
}

function getAllCategoryNames(stats: GameData[]) {
    let knownCategoryNames: string[] = [];

    each(stats, stat => {
        each (stat.objectives, objective => {
            if (knownCategoryNames.includes(objective.category) === false) {
                knownCategoryNames.push(objective.category);
            }
        });
    });

    return knownCategoryNames;
}

function flattenObjectiveResults(allCats, plays, classSize) {
    let totalValuesForBestPlay: number[] = [];

    let uniqueUsers: string[] = [];

    // Initialze this to the number of categories
    each(allCats, cat => {
        totalValuesForBestPlay.push(0);
    });

    each(plays, (play: any) => {
        const uid: string = play['user-id'];

        if (uniqueUsers.includes(uid) === false) {
            uniqueUsers.push(uid);
        }

        for (let i = 0; i < allCats.length; i++) {
            const categoryName = allCats[i];
            if (play.categories[categoryName]) {
                const playValueForThisCategory = play.categories[categoryName].percent;

                totalValuesForBestPlay[i] += playValueForThisCategory;
            }
        }
    });

    let correctClassSize = uniqueUsers.length;

    if (correctClassSize === 0) { correctClassSize = 1; }

    // average them all out
    for (let i = 0; i < totalValuesForBestPlay.length; i++) {
        if (totalValuesForBestPlay[i] !== 0) {
            totalValuesForBestPlay[i] = Math.round(totalValuesForBestPlay[i] / correctClassSize);
        }
    }

    return totalValuesForBestPlay;
}

export class ProgressView extends React.Component<Props, State> {

    state: State = {
        loadingStats: true,
        stats: undefined,
        classEnrollment: undefined
    }

    getStatsForScenario = (scenarioName: string) => {
        const {
            stats
        } = this.state;
        
        return filter(stats, stat => {
            return stat.scenario === scenarioName
        });
    }

    componentDidMount() {
        const {
            classID
        } = this.props;
        
        this.loadClassData(classID);
    }

    loadClassData = (classID: string) => {
        // Load the stats
        executeQueryGetAuth("data/all/class/results/" + classID.toString(), {}, this.props.user, (classStatsResponse) => {
            executeQueryGetAuth("class/" + classID.toString() + "/students", {}, this.props.user, (response) => {
                let classEnrollmentBase: any[] = [];

                if (this.props.user.level === 3) {
                    classEnrollmentBase = response.body;
                }
                else {
                    // Filter it out so only 
                    each(response.body, en => {
                        if (en.level === 1) {
                            classEnrollmentBase.push(en);
                        }
                    })
                }

                executeQueryGetAuth("progress/class/" + classID.toString() + "/overview", {}, this.props.user, (data) => {
                    const results = JSON.parse(data.text);

                    for(let i = 0; i < classEnrollmentBase.length; i++) {
                        const studentID = classEnrollmentBase[i]["id"];

                        classEnrollmentBase[i]["overview"] = results[studentID];
                    }

                    this.setState({
                        loadingStats: false,
                        classEnrollment: classEnrollmentBase,
                        stats: JSON.parse(classStatsResponse.text)
                    });
                });

                // for(let i = 0; i < classEnrollmentBase.length; i++) {
                //     const studentID = classEnrollmentBase[i]["id"];

                //     for (let c = 0; c < result.length; c++) {
                //         if (studentID === result[c]["id"]) {
                //             classEnrollmentBase[i]["overview"] = result[c]["payload"];
                //         }
                //     }
                // }

                // this.setState({
                //     loadingStats: false,
                //     classEnrollment: classEnrollmentBase,
                //     stats: JSON.parse(classStatsResponse.text)
                // });

                /*let studentDataGatherPromises: any[] = [];

                // Now Load all the students
                each(response.body, student => {
                    const studentID = student["id"];

                    studentDataGatherPromises.push(
                        new Promise((resolve, reject) => {
                            executeQueryGetAuth("progress/user/" + studentID + "/overview", {}, this.props.user, (data) => {
                                resolve({
                                    id: studentID, payload:data.body
                                });
                            })
                        })
                    );
                });

                Promise.all(studentDataGatherPromises).then((result) => {
                    for(let i = 0; i < classEnrollmentBase.length; i++) {
                        const studentID = classEnrollmentBase[i]["id"];

                        for (let c = 0; c < result.length; c++) {
                            if (studentID === result[c]["id"]) {
                                classEnrollmentBase[i]["overview"] = result[c]["payload"];
                            }
                        }
                    }

                    this.setState({
                        loadingStats: false,
                        classEnrollment: classEnrollmentBase,
                        stats: JSON.parse(classStatsResponse.text)
                    });
                });*/
            });
        });
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (!isEqual(nextProps.classID, this.props.classID)) {
            this.setState({
                loadingStats: true
            });
            
            this.loadClassData(this.props.classID);

            return true;
        }

        if (!isEqual(nextState, this.state)) {
            return true;
        }

        return false;
    }

    renderScenarioBestExplore = () => {
        const {
            classEnrollment
        } = this.state;
        
        const allCats = getAllCategoryNames(this.getStatsForScenario(this.props.scenario));

        const bestAsssessPlays = getBestPlaysForStudents(this.getStatsForScenario(this.props.scenario + "_assess"));
        const totalValuesOfAssessPlays = flattenObjectiveResults(allCats, bestAsssessPlays, classEnrollment.length);

        const bestExplorePlays = getBestPlaysForStudents(this.getStatsForScenario(this.props.scenario));
        const totalValuesForBestExplorePlay = flattenObjectiveResults(allCats, bestExplorePlays, classEnrollment.length);

        let chartConfig = {
            chart: {
                type: 'column'
            },
            title: {
                text: 'Best Explore compared to Best Assess Play'
            },
            subtitle: {
                text: 'Student Assessment'
            },
            xAxis: {
                categories: allCats,
                crosshair: true
            },
            credits: {
                enabled: false
            },
            yAxis: {
                min: 0,
                max: 100,
                title: {
                    text: 'Percentage of Class Achieved'
                }
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0
                }
            },
            series: [{
                name: 'Best Explore Play',
                data: totalValuesForBestExplorePlay
        
            }, {
                name: 'Best Assess Play',
                data: totalValuesOfAssessPlays
            }]
        }

        return <Card interactive={false} elevation={Elevation.ONE}>
            <ReactHighcharts highcharts={Highcharts} options={chartConfig} />
        </Card>
    }

    renderBestPlayOverTime = () => {
        const allPlaysByStudents = sortStatBlock(
            this.getStatsForScenario(this.props.scenario).concat(this.getStatsForScenario(this.props.scenario + "_assess"))
            .concat(this.getStatsForScenario(this.props.scenario + "_initial")));

        let longestString = 0;
        let userMapOfPlays = {};
        let uniqueFixer: any[] = [];

        each(allPlaysByStudents, stat => {
            const userID = stat['user-id'];
            const gameID = stat['game-id'];

            if (uniqueFixer.includes(gameID) === false) {
                uniqueFixer.push(gameID);

                const statScore = parseFloat(stat['results']) * 100.0;

                if (userMapOfPlays[userID] === undefined) {
                    userMapOfPlays[userID] = [];
                }

                userMapOfPlays[userID].push(statScore);

                if (userMapOfPlays[userID].length > longestString) {
                    longestString = userMapOfPlays[userID].length;
                }
            }
        });

        let averagesBySlot: number[] = [];

        for (let i = 0; i < longestString; i++) {
            let entriesInThisSlot = 0;
            let scoreForThisSlot = 0;

            each(userMapOfPlays, user => {
                if (user[i] !== undefined) {
                    entriesInThisSlot += 1;
                    scoreForThisSlot += parseFloat(user[i]);
                }
            });

            if (entriesInThisSlot > 0) {
                averagesBySlot.push(Math.round(scoreForThisSlot / entriesInThisSlot));
            }
        }

        let chartConfig = {
            chart: {
                type: 'column'
            },
            title: {
                text: 'Average Play By Attempt'
            },
            subtitle: {
                text: 'Student Assessment'
            },
            credits: {
                enabled: false
            },
            xAxis: {
                min: 1,
                title: {
                    text: 'Plays Attempted'
                }
            },
            yAxis: {
                title: {
                    text: 'Average Score'
                },
                min: 0,
                max: 100
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0
                }
            },
            series: [{
                name: 'Average Play',
                data: averagesBySlot
            }]
        }

        return <Card interactive={false} elevation={Elevation.ONE}>
            <ReactHighcharts highcharts={Highcharts} options={chartConfig} />
        </Card>
    }

    renderScenarioInitialRadarGraph = () => {
        const {
            classEnrollment
        } = this.state;
        
        const stats = this.getStatsForScenario(this.props.scenario + "_initial");
        const allCats = getAllCategoryNames(stats);
        const initialPlays = getInitialPlaysForStudents(stats);
        const totalValuesForInitalPlay = flattenObjectiveResults(allCats, initialPlays, classEnrollment.length);


        const bestExploreStats = this.getStatsForScenario(this.props.scenario);
        const bestAssessStats  = this.getStatsForScenario(this.props.scenario + "_assess");
        const combinedBestStats = bestAssessStats.concat(bestExploreStats);

        const bestAsssessPlays = getBestPlaysForStudents(combinedBestStats);
        const totalValuesForAssessPlay = flattenObjectiveResults(allCats, bestAsssessPlays, classEnrollment.length);

        let chartConfig = {
            chart: {
                polar: true,
                height: 800
            },
            pane: {
                startAngle: 0,
                endAngle: 360
            },
            title: {
                text: 'Initial Play compared to Best Play'
            },
            subtitle: {
                text: 'Student Growth'
            },
            xAxis: {
                tickInterval: 1,
                min: 0,
                max: allCats.length,
                categories: allCats
            },
            plotOptions: {
                series: {
                    pointStart: 0,
                    pointInterval: 1,
                },
            },
            credits: {
                enabled: false
            },
            yAxis: {
                min: 0,
                max: 100
            },
            series: [{
                name: 'Initial Play',
                type: 'area',
                data: totalValuesForInitalPlay
            },{
                name: "Best Play",
                type: 'area',
                data: totalValuesForAssessPlay
            }]
        }

        return <Card interactive={false} elevation={Elevation.ONE}>
            <ReactHighcharts highcharts={Highcharts} options={chartConfig} />
        </Card>
    }

    renderScenarioInitialChart = () => {
        const {
            classEnrollment
        } = this.state;
        
        const stats = this.getStatsForScenario(this.props.scenario);
        const allCats = getAllCategoryNames(stats);
        const initialPlays = getInitialPlaysForStudents(this.getStatsForScenario(this.props.scenario));
        const bestPlays = getBestPlaysForStudents(stats);

        const totalValuesForInitalPlay = flattenObjectiveResults(allCats, initialPlays, classEnrollment.length);
        const totalValuesForBestPlay = flattenObjectiveResults(allCats, bestPlays, classEnrollment.length);

        let chartConfig = {
            chart: {
                type: 'column'
            },
            title: {
                text: 'Initial Play compared to Best Play'
            },
            xAxis: {
                categories: allCats
            },
            credits: {
                enabled: false
            },
            yAxis: {
                min: 0,
                max: 100,
                title: {
                    text: 'Percentage of Class Achieved'
                }
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0
                }
            },
            series: [{
                name: 'Initial Play',
                data: totalValuesForInitalPlay
        
            }, {
                name: 'Best Play',
                data: totalValuesForBestPlay
            }]
        }

        return <Card interactive={false} elevation={Elevation.ONE}>
            <ReactHighcharts highcharts={Highcharts} options={chartConfig} />
        </Card>
    }

    getStudentsWithPlaysLessThanFourMinutesApart = () => {
        const {
            classEnrollment
        } = this.state;

        const stats = sortStatBlock(this.getStatsForScenario(this.props.scenario));

        let timeSets = {};

        each(stats, (play: any) => {
            const uid: string = play['user-id'];
            const created = moment(play['submitted']);

            if (timeSets[uid] === undefined) {
                timeSets[uid] = [];
            }

            timeSets[uid].push(created);
        });

        let returnSet: any[] = [];

        Object.keys(timeSets).forEach((userKey) => {
            const userSet = timeSets[userKey];

            if (userSet.length >= 2) {
                let shortestLength = userSet[1].diff(userSet[0]);

                for (let i = 0; i < userSet.length; i++) {
                    if (userSet[i] !== undefined) {
                        if (userSet[i + 1] !== undefined) {
                            let newLength = userSet[i+1].diff(userSet[i]);

                            if (newLength < shortestLength) {
                                shortestLength = newLength;
                            }
                        }
                    }
                }

                if (shortestLength <= 240000) { // 5 minutes
                    each(classEnrollment, classEnrollmentEntry => {
                        if (classEnrollmentEntry.id.toString() === userKey) {
                            returnSet.push({
                                name: classEnrollmentEntry.first + " " + classEnrollmentEntry.last,
                                email: classEnrollmentEntry.email,
                                none: 'Recommend taking more time between scenarios to consider the outcomes.'
                            });
                        }
                    });
                }
            }
        });

        return returnSet;
    }

    renderFatigueStudents = () => {
        const data = this.getStudentsWithPlaysLessThanFourMinutesApart();

        const columns = [{
            Header: "Student",
            accessor: "name"
        }, {
            Header: "Email",
            accessor: "email"
        },{
            Header: "Recommended Coaching",
            accessor: "none"
        }];

        return <Card interactive={false} elevation={Elevation.ONE}>
            <H4>High Fatigue Students</H4> <span>(Students at risk from playing too many scenarios back to back)</span>

            <ReactTable data={data} sortable={false} pageSize={data.length} showPageSizeOptions={false} showPagination={false} columns={columns} />
        </Card>
    }

	render() {
        const {
            loadingStats
        } = this.state;

        if (loadingStats) return <Spinner />

        return <span>
            {/* {this.renderFatigueStudents()} */}
            {this.renderScenarioInitialChart()}
            {this.renderScenarioBestExplore()}
            {this.renderBestPlayOverTime()}
            {this.renderScenarioInitialRadarGraph()}
        </span>
	}
}
